import gsap from 'gsap';
import menu, { MenuCloseReason, MenuEvent, MenuState } from './menu';
import menuHorizontal from './menu-horizontal';
import inPageLinks from './in-page-links';
import { PageEvent } from './page-lifecycle/PageEvent';
import { HHDSButtonHeader, HHDSButtonHeaderTagName } from '../components/ButtonHeader/ButtonHeader';
import { barbaController } from './page-lifecycle/barba/BarbaController';
import { forceFocusReset } from '../components/ComponentUtils';
import { CSSBreakpoint } from './constants-theme';

const enum HeaderState {
	Inline = 'inline',
	Scrolled = 'scrolled',
}

const DEBUG_VERBOSE = false;

export class HHDSHeader {
	private _header: HTMLElement | null = null;
	private _inline: HTMLElement | null = null;
	private _scrolled: HTMLElement | null = null;
	private _content: HTMLElement | null = null;
	private _inPageLinksEl: HTMLElement | null = null;

	private _burgerHeadingMenuEl!: HTMLElement;

	private _burgerBtn: HHDSButtonHeader | null = null;
	private _weglotBtn: HTMLInputElement | null = null;

	private _headerState: HeaderState = HeaderState.Inline;
	private _scrolledHeaderTween: gsap.core.Tween | null = null;

	private _scrolledHeaderRetracted: boolean = false;

	private _defaultMenuText: string = '';
	private _defaultMenuLabel: string = '';
	private _closeMenuText: string = '';
	private _closeMenuLabel: string = '';

	public element: HTMLElement | null = null;

	constructor() {}

	// ////////////////////////////////////////////////////////////////////
	// This needed surfaced so the scrolledHeader could be specifically targeted
	// this.element was being updated but a timing issue may result in it being the wrong element
	// Related to this issue: https://chunkgroup.mydonedone.com/issuetracker/projects/89939/issues/124

	get scrolledHeader() {
		return this._scrolled;
	}

	// ////////////////////////////////////////////////////////////////////

	init() {
		this._header = document.querySelector('header');
		if (!this._header) {
			return;
		}

		this.element = this._inline = this._header.querySelector('.header__inline') as HTMLElement;

		this._scrolled = this._header.querySelector('.header__scrolled') as HTMLElement;
		this._content = this._header.querySelector('.page-content') as HTMLElement;

		this._inPageLinksEl = document.querySelector('.in-page-links') as HTMLElement;

		this._burgerBtn = this._header.querySelector('#header-burger') as HHDSButtonHeader;
		this._burgerHeadingMenuEl = this._header.querySelector('#burger-heading-menu') as HTMLElement;
		this._burgerHeadingMenuEl = this._header.querySelector('#burger-heading-menu') as HTMLElement;
		const burgerHeadingCloseEl = this._header.querySelector('#burger-heading-close') as HTMLElement;
		this._closeMenuText = burgerHeadingCloseEl.textContent || '';
		this._defaultMenuText = this._burgerHeadingMenuEl.textContent || '';

		const menuLabelEl = this._header.querySelector('#burger-heading-menu-label') as HTMLElement;
		const closeLabelEl = this._header.querySelector('#burger-heading-close-label') as HTMLElement;

		this._defaultMenuLabel = menuLabelEl?.textContent ?? '';
		this._closeMenuLabel = closeLabelEl?.textContent ?? '';

		burgerHeadingCloseEl?.remove();
		menuLabelEl?.remove();
		closeLabelEl?.remove();

		this.updateBurgerHeadingMenuEl(this._defaultMenuText, this._defaultMenuLabel);

		this._weglotBtn = document.querySelector('#header__weglot .weglot_choice') as HTMLInputElement;

		menu.addEventListener(MenuEvent.opening, () => {
			this.updateBurgerHeadingMenuEl(this._closeMenuText, this._closeMenuLabel);

			gsap.fromTo(this._burgerHeadingMenuEl, { opacity: 0, x: -10 }, { duration: 0.5, opacity: 1, x: 0 });
			this.updateHeaderButtonStyles();
		});

		menu.addEventListener(MenuEvent.closed, () => {
			this.updateBurgerHeadingMenuEl(this._defaultMenuText, this._defaultMenuLabel);

			gsap.fromTo(this._burgerHeadingMenuEl, { opacity: 0, x: -10 }, { duration: 0.5, opacity: 1, x: 0 });
			this.updateHeaderButtonStyles();

			forceFocusReset();
		});

		menu.addEventListener(MenuEvent.reverseComplete, (event: any) => {
			if (event.detail?.viaPageTransition) {
				if (this._headerState == HeaderState.Scrolled) {
					this.hideScrolledHeader(0.3).then(() => {
						this.setHeaderState(HeaderState.Inline);
					});
				} else {
					this.setHeaderState(HeaderState.Inline);
				}
			}
		});

		menuHorizontal.init();
		inPageLinks.init();

		this.updateHeaderButtonStyles();

		if (this._inline && this._scrolled) {
			this.addListeners();
		}
	}

	private updateBurgerHeadingMenuEl(text: string, label: string) {
		if (this._burgerHeadingMenuEl) {
			this._burgerHeadingMenuEl.textContent = text;
			this._burgerHeadingMenuEl.setAttribute('aria-label', label);
		}
	}

	private updateHeaderButtonStyles(): void {
		const menuOpenOrAnimating: boolean = menu.state == MenuState.Open || menu.state == MenuState.Animating;
		const headerInLightMode: boolean = this._header!.classList.contains('light');
		const inlineHeader: boolean = this._headerState === HeaderState.Inline;
		const useLightUnderline: boolean = (inlineHeader && !headerInLightMode) || menuOpenOrAnimating;

		this._header?.querySelectorAll(HHDSButtonHeaderTagName)?.forEach((button) => {
			button.classList.toggle(`${HHDSButtonHeaderTagName}--light`, useLightUnderline);
		});
	}

	addListeners(): void {
		let ticking = false;

		window.addEventListener('scroll', () => {
			if (!ticking) {
				// Queue the callback function to be called before the next repaint
				requestAnimationFrame(() => {
					const { scrollY } = window;

					this.closeWeglot();

					if (menu.state != MenuState.Animating && !barbaController.transitionActive) {
						if (scrollY > this.togglePositionY) {
							if (this._headerState === HeaderState.Inline) {
								this.setHeaderState(HeaderState.Scrolled);
								this.showScrolledHeader();
							}
						} else {
							if (this._headerState === HeaderState.Scrolled) {
								this.setHeaderState(HeaderState.Inline);
							}
						}
					} else if (menu.state == MenuState.Closed) {
						this.setHeaderState(HeaderState.Inline);
					}

					// Reset the ticking flag
					ticking = false;
				});
				// Set the ticking flag to true to prevent multiple calls
				ticking = true;
			}
		});

		document.addEventListener(PageEvent.leave, (_event: any) => {
			if (menu.state == MenuState.Closed) this.hideScrolledHeader();
		});
		document.addEventListener(PageEvent.enterComplete, (_event: any) => {
			DEBUG_VERBOSE &&
				console.log(
					'PageEvent.enterComplete',
					`document.body.classList.contains('light')`,
					document.body.classList.contains('light'),
					!document.body.classList.contains('light')
				);

			if (menu.state == MenuState.Closed) {
				DEBUG_VERBOSE && console.log('Menu is NOT animating; will set header state to Inline');
				this.setHeaderState(HeaderState.Inline);
			}

			this._header?.classList.toggle('light', !document.body.classList.contains('light'));

			if (this._content) gsap.set(this._content, { y: 0 });

			menu.close(MenuCloseReason.PageTransition);

			this.updateHeaderButtonStyles();
		});

		this._burgerBtn?.addEventListener('click', () => {
			if (menu.state == MenuState.Closed) {
				if (window.scrollY > 0 && window.scrollY < this.togglePositionY && this._headerState == HeaderState.Inline) {
					window.scrollTo({ top: 0, behavior: 'smooth' });
				}
				menu.open();
			} else {
				menu.close(MenuCloseReason.UserInteraction);
			}
		});
	}

	setHeaderState(state: HeaderState): void {
		if (state == this._headerState) return;
		this._headerState = state;
		DEBUG_VERBOSE && console.log('setHeaderState', state);

		if (this._inline && this._scrolled) {
			this._scrolled.classList.toggle('header--active', state === HeaderState.Scrolled);
			this._inline.classList.toggle('header--active', state === HeaderState.Inline);

			// In-page links
			if (this._inPageLinksEl) {
				this._inPageLinksEl.classList.toggle('active', state === HeaderState.Scrolled);
				this._inPageLinksEl.setAttribute('aria-hidden', state === HeaderState.Scrolled ? 'false' : 'true');
				inPageLinks.setTabIndexOnLinks(state === HeaderState.Scrolled);
			} else {
				console.warn(`Unable to derive 'in-page-links'`);
			}

			if (state === HeaderState.Inline) {
				if (this._inline && this._scrolled) {
					this._inline?.replaceChildren(...this._scrolled.childNodes);
					this.element = this._inline;
				}
			} else {
				if (this._inline && this._scrolled) {
					this._scrolled?.replaceChildren(...this._inline.childNodes);
					this.element = this._scrolled;
					gsap.set(this._scrolled, { translateY: '-100%' });
					this.showScrolledHeader(true);
				}
			}

			this.updateHeaderButtonStyles();
		}
	}

	showScrolledHeader(force: boolean = false): void {
		if (!this._scrolledHeaderRetracted && !force) return;
		this._scrolledHeaderTween?.kill();
		this._scrolledHeaderRetracted = false;
		this._scrolledHeaderTween = gsap.to(this._scrolled, { translateY: '0%', duration: 0.6 });
	}

	hideScrolledHeader(duration: number = 0.6): Promise<void> {
		return new Promise((resolve) => {
			if (this._scrolledHeaderRetracted) {
				resolve();
				return;
			}
			this._scrolledHeaderTween?.kill();
			this._scrolledHeaderRetracted = true;
			this._scrolledHeaderTween = gsap.to(this._scrolled, { translateY: '-100%', duration: duration, onComplete: resolve });
		});
	}

	private closeWeglot(): void {
		this._weglotBtn && this._weglotBtn?.checked == true ? (this._weglotBtn.checked = false) : null;
	}

	// It's hard to measure the height of the horizontal menu due to it's absolutely positioned content so we're using a constant
	get togglePositionY() {
		const isDesktop = window.matchMedia(`(min-width: ${CSSBreakpoint.Desktop}px)`).matches;

		const value = isDesktop ? 100 : this.element!.clientHeight;

		return value;
	}
}

export default new HHDSHeader();
